import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Modal,
  Backdrop,
  Slider,
  Collapse,
  IconButton,
  Paper,
  Grid,
  CircularProgress,
  LinearProgress,
  Dialog
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import EventCreationDialog from './EventCreationDialog';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import MapboxDestinationSearch from './MapboxDestinationSearch';
import { addDays, format, differenceInDays, differenceInMinutes } from 'date-fns';
import { generateTravelPlan } from '../services/api';

const TravelPlanForm = ({ onSubmit, onCancel, initialData = null }) => {
  // Travel style options
  const travelStyles = [
    { emoji: '🏖️', name: 'Relaxation' },
    { emoji: '🎒', name: 'Adventure' },
    { emoji: '🏛️', name: 'Cultural' },
    { emoji: '🍴', name: 'Culinary' },
    { emoji: '💼', name: 'Business' },
    { emoji: '👨‍👩‍👧‍👦', name: 'Family' },
    { emoji: '💑', name: 'Romantic' },
    { emoji: '🏃', name: 'Sports' },
    { emoji: '🌿', name: 'Eco-Tourism' },
    { emoji: '🎉', name: 'Festival' },
    { emoji: '🧘', name: 'Wellness' },
    { emoji: '📸', name: 'Photography' }
  ];

  // Initialize form data
  const safeInitialData = initialData ? {
    title: initialData.title || '',
    startDate: initialData.startDate || null,
    endDate: initialData.endDate || null,
    destination: initialData.destination || '',
    destinations: initialData.destinations || (initialData.destination ? 
      [{ id: 1, name: initialData.destination, shortName: initialData.destination, coordinates: [] }] : []),
    description: initialData.description || '',
    privacy: 'private', // Default to private
    tags: Array.isArray(initialData.tags) ? initialData.tags : [],
    dayCount: initialData.dayCount || 1,
    totalBudget: initialData.totalBudget || 3000,
    currentSpent: initialData.currentSpent || 0,
    events: initialData.events || [],
    travelerAge: initialData.travelerAge || initialData.traveler_age || 30,
    wakeUpTime: initialData.wakeUpTime || '09:00',
    numberOfTravelers: initialData.numberOfTravelers || initialData.number_of_travelers || 1
  } : null;
  
  const [formData, setFormData] = useState(safeInitialData || {
    title: '',
    startDate: null,
    endDate: null,
    destination: '',
    destinations: [],
    description: '',
    privacy: 'private',
    tags: [],
    dayCount: 1,
    totalBudget: 3000,
    currentSpent: 0,
    events: [],
    travelerAge: 30,
    wakeUpTime: '09:00',
    numberOfTravelers: 1
  });

  const [selectedStyles, setSelectedStyles] = useState([]);
  const [expandedDays, setExpandedDays] = useState({ 1: true });
  const [loadingAI, setLoadingAI] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Packing up luggage...');
  
  // Event dialog state
  const [openEventDialog, setOpenEventDialog] = useState(false);
  const [selectedDay, setSelectedDay] = useState(1);

  // Initialize selected styles from tags
  useEffect(() => {
    if (formData.tags.length > 0) {
      const styles = formData.tags.filter(tag => 
        travelStyles.some(style => style.name === tag)
      );
      setSelectedStyles(styles);
    }
  }, [formData.tags]);

  // Auto-calculate day count when dates change
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      // Only run calculation if both dates are valid numbers
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        const days = differenceInDays(end, start) + 1;
        setFormData(prev => ({
          ...prev,
          dayCount: Math.max(1, days)
        }));
      }
    }
  }, [formData.startDate, formData.endDate]);

  useEffect(() => {
    if (loadingAI) {
      setProgress(0);
      
      const messages = [
        "Packing up luggage...",
        "Looking for sunglasses...",
        "Checking the weather forecast...",
        "Finding the best local snacks...",
        "Scouting hidden gems...",
        "Polishing the pelican's beak...",
        "Consulting the travel maps...",
        "Booking the perfect sunset view..."
      ];
      
      setLoadingMessage(messages[0]);
      
      // Progress Bar Timer (Caps at 90% until done)
      const progressInterval = setInterval(() => {
        setProgress(oldProgress => {
          if (oldProgress >= 90) return 90;
          const diff = Math.random() * 9;
          return Math.min(oldProgress + diff, 90);
        });
      }, 4000);

      // Message Rotation Timer
      const messageInterval = setInterval(() => {
        setLoadingMessage(prev => {
          const currentIndex = messages.indexOf(prev);
          const nextIndex = (currentIndex + 1) % messages.length;
          return messages[nextIndex];
        });
      }, 10000);

      return () => {
        clearInterval(progressInterval);
        clearInterval(messageInterval);
      };
    }
  }, [loadingAI]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDestinationsChange = (newDestinations) => {
    setFormData(prev => ({
      ...prev,
      destinations: newDestinations,
      destination: newDestinations.map(dest => dest.shortName).join(', ') // Keep compatibility with existing destination field
    }));
  };

  const handleStyleToggle = (styleName) => {
    const newStyles = selectedStyles.includes(styleName)
      ? selectedStyles.filter(style => style !== styleName)
      : [...selectedStyles, styleName];
    
    setSelectedStyles(newStyles);
    setFormData(prev => ({
      ...prev,
      tags: newStyles
    }));
  };

  const handleBudgetChange = (_, newValue) => {
    setFormData(prev => ({
      ...prev,
      totalBudget: newValue
    }));
  };

  const toggleDayExpansion = (day) => {
    setExpandedDays(prev => ({
      ...prev,
      [day]: !prev[day]
    }));
  };

  const addNewDay = () => {
    const newDayCount = formData.dayCount + 1;
    setFormData(prev => ({
      ...prev,
      dayCount: newDayCount
    }));
    setExpandedDays(prev => ({
      ...prev,
      [newDayCount]: true
    }));
  };

  const handleDirectAIGeneration = async () => {
    // Basic validation
    const destinationName = formData.destination || formData.destinations?.[0]?.shortName || formData.destinations?.[0]?.name || '';
    if (!formData.startDate || !destinationName || selectedStyles.length === 0) {
      window.alert('Please add a start date, destination, and at least one travel style before generating with AI.');
      return;
    }

    const start = new Date(formData.startDate);
    if (isNaN(start.getTime())) {
      window.alert('Please provide a valid start date before generating.');
      return;
    }
    const end = formData.endDate ? new Date(formData.endDate) : null;

    const payload = {
      arriveDateTime: start.toISOString(),
      departDateTime: end ? end.toISOString() : null,
      destination: destinationName,
      travelPreferences: selectedStyles,
      wakeUpTime: formData.wakeUpTime,
      traveler_age: formData.travelerAge,
      trip_length: formData.dayCount,
      // Budget/party details
      budget_per_person: formData.totalBudget,
      number_of_travelers: formData.numberOfTravelers,
      travel_type: 'leisure'
    };

    try {
      setLoadingAI(true);
      const response = await generateTravelPlan(payload);
      const { travelPlan, aiResponse } = response.data || {};
      
      console.log("AI Response Received:", response.data); // Debug log

      if (travelPlan) {
        // 1. Determine Events: Use backend events, or fallback to parsing aiResponse if empty
        let finalEvents = travelPlan.events || [];
        
        // Fallback: If backend failed to save events but sent aiResponse, map it manually
        if (finalEvents.length === 0 && aiResponse?.dailyItineraries) {
          console.log("Using fallback event parsing from aiResponse");
          aiResponse.dailyItineraries.forEach(dayPlan => {
            // Map activities
            if (dayPlan.activities) {
              dayPlan.activities.forEach(act => {
                finalEvents.push({
                  title: act.activityName || 'Activity',
                  description: act.description,
                  location: act.location,
                  start: null, // AI usually gives ranges like "10:00-12:00", tough to parse to Date strictly here without more logic
                  day: dayPlan.day || 1,
                  category: 'activity'
                });
              });
            }
            // Map meals
            if (dayPlan.meals) {
               Object.entries(dayPlan.meals).forEach(([mealType, mealDesc]) => {
                 finalEvents.push({
                   title: `${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`,
                   description: mealDesc,
                   day: dayPlan.day || 1,
                   category: 'meal'
                 });
               });
            }
          });
        }

        const newDayCount = travelPlan.dayCount || formData.dayCount;

        // 2. Update Form Data
        setFormData(prev => ({
          ...prev,
          title: travelPlan.title || aiResponse?.title || prev.title || destinationName || 'My Travel Plan',
          description: travelPlan.description || aiResponse?.summary || prev.description,
          destination: travelPlan.destination || destinationName || prev.destination,
          destinations: travelPlan.destinations || prev.destinations,
          startDate: travelPlan.startDate ? new Date(travelPlan.startDate) : start,
          endDate: travelPlan.endDate ? new Date(travelPlan.endDate) : (end || prev.endDate),
          dayCount: newDayCount,
          totalBudget: travelPlan.totalBudget ?? prev.totalBudget,
          currentSpent: travelPlan.currentSpent ?? prev.currentSpent,
          events: finalEvents, // Use our robust events list
          privacy: 'private',
          tags: selectedStyles,
          wakeUpTime: travelPlan.wakeUpTime || prev.wakeUpTime,
          travelerAge: travelPlan.traveler_age ?? travelPlan.travelerAge ?? prev.travelerAge
        }));

        // 3. Auto-Expand All Days (So user sees the generated content immediately)
        const newExpandedDays = {};
        for (let i = 1; i <= newDayCount; i++) {
          newExpandedDays[i] = true;
        }
        setExpandedDays(newExpandedDays);
      }
    } catch (err) {
      console.error('AI generation failed:', err);
      const message = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to generate travel plan. Please try again.';
      window.alert(message);
    } finally {
      setLoadingAI(false);
    }
  };

  // Event dialog handlers
  const handleAddEventToDay = (dayNumber) => {
    setSelectedDay(dayNumber);
    setOpenEventDialog(true);
  };

  const handleCloseEventDialog = () => {
    setOpenEventDialog(false);
  };

  const handleEventSubmit = (eventData) => {
    setFormData(prev => ({
      ...prev,
      events: [...(prev.events || []), eventData]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Calculate end date if not set
    const endDate = formData.endDate || (formData.startDate ? addDays(new Date(formData.startDate), formData.dayCount - 1) : null);
    
    onSubmit({
      ...formData,
      endDate: endDate,
      title: formData.title || formData.destination || formData.destinations.map(d => d.shortName).join(', ') || 'My Travel Plan'
    });
  };

  const getDayDate = (dayNumber) => {
    if (!formData.startDate) return '';
    const date = addDays(new Date(formData.startDate), dayNumber - 1);
    return format(date, 'MMMM d, yyyy');
  };

  const dayEvents = formData.events || [];
  const groupedEvents = dayEvents.reduce((acc, event) => {
    const day = event.day || 1;
    if (!acc[day]) acc[day] = [];
    acc[day].push(event);
    return acc;
  }, {});

  return (
    <Modal
      open={true}
      onClose={onCancel}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(4px)',
          }
        }
      }}
      disableAutoFocus={false}
      disableEnforceFocus={false}
      disableRestoreFocus={false}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: { xs: '10px', sm: '20px' },
        overflow: 'auto',
        paddingTop: { xs: '20px', sm: '40px' }
      }}
    >
      <Box 
        tabIndex={-1}
        sx={{
          backgroundColor: '#FFF5E6',
          minHeight: 'auto',
          maxHeight: 'calc(100vh - 60px)',
          width: '100%',
          maxWidth: { xs: '100%', sm: '95%', md: '1200px' },
          color: '#4A5568',
          position: 'relative',
          overflow: 'auto',
          borderRadius: { xs: '10px', sm: '20px' },
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
          marginBottom: '40px',
          outline: 'none' // Remove focus outline on modal container
        }}
      >
        {/* Header with Pelican Beaks branding */}
        <Box sx={{
          background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 50%, #FFA07A 100%)',
          padding: '20px 24px',
          borderRadius: { xs: '10px 10px 0 0', sm: '20px 20px 0 0' },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
          }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48,
                height: 48,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                <img 
                  src={process.env.PUBLIC_URL + '/PelicanLogo.ico'} 
                  alt="Pelican Beaks Logo"
                  style={{ width: 36, height: 36 }}
                />
              </Box>
              <Box>
                <Typography variant="h4" sx={{
                  color: 'white',
                  fontWeight: 700,
                  letterSpacing: '-0.5px',
                  fontSize: { xs: '20px', sm: '24px' }
                }}>
                  Pelican Beaks
                </Typography>
                <Typography variant="body2" sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  AI Travel Planner
                </Typography>
              </Box>
            </Box>
            {onCancel && (
              <IconButton 
                onClick={onCancel}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    transform: 'scale(1.05)'
                  },
                  width: 40,
                  height: 40
                }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ 
          maxWidth: '1000px', // Slightly smaller for better fit
          margin: '0 auto', 
          padding: { xs: '20px 15px', sm: '40px 20px' }, // Responsive padding
          position: 'relative'
        }}>
          {/* Header Section */}
          <Box sx={{ 
            textAlign: 'center', 
            marginBottom: { xs: '30px', sm: '40px' },
            px: { xs: 2, sm: 3 }
          }}>
            <Typography 
              id="travel-plan-form-title"
              variant="h2" 
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 50%, #FFA07A 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '15px',
                letterSpacing: '-1px'
              }}
            >
              Create Your Dream Trip
            </Typography>
            <Typography variant="h6" sx={{ 
              fontSize: { xs: '1rem', sm: '1.2rem' },
              color: '#666',
              fontWeight: 400
            }}>
              Let AI help you plan the perfect adventure
            </Typography>
          </Box>

          {/* Form Container */}
          <Paper component="form" onSubmit={handleSubmit} sx={{
            background: 'white',
            borderRadius: { xs: '20px', sm: '30px' },
            padding: { xs: '30px 20px', sm: '40px 30px', md: '50px' },
            border: '1px solid rgba(255, 122, 107, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
            mx: { xs: 2, sm: 3 }
          }}>
            {/* Form Grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(300px, 1fr))' }, // Responsive grid
              gap: { xs: '20px', sm: '30px' }, // Responsive gap
              marginBottom: { xs: '30px', sm: '40px' } // Responsive margin
            }}>
              {/* Destinations */}
              <Box>
                <Typography sx={{
                  display: 'block',
                  marginBottom: '10px',
                  fontWeight: 600,
                  color: '#4A5568',
                  fontSize: '0.95rem',
                  letterSpacing: '0.5px'
                }}>
                  Destinations
                </Typography>
                <MapboxDestinationSearch
                  destinations={formData.destinations}
                  onDestinationsChange={handleDestinationsChange}
                  placeholder="Search for places to visit..."
                  maxDestinations={5}
                />
              </Box>

              {/* Travel Dates */}
              <Box>
                <Typography sx={{
                  display: 'block',
                  marginBottom: '10px',
                  fontWeight: 600,
                  color: '#4A5568',
                  fontSize: '0.95rem',
                  letterSpacing: '0.5px'
                }}>
                  Travel Dates
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Start Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Start Date"
                        value={formData.startDate}
                        onChange={(date) => handleInputChange('startDate', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            // Block typing to prevent crashes and force picker usage
                            onKeyDown: (e) => e.preventDefault(),
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#FF7B6B' },
                                '&.Mui-focused fieldset': { borderColor: '#FF7B6B' },
                                '& input': {
                                  cursor: 'pointer',
                                  caretColor: 'transparent'
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  
                  {/* End Date */}
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="End Date"
                        value={formData.endDate}
                        onChange={(date) => handleInputChange('endDate', date)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            // Block typing to prevent crashes and force picker usage
                            onKeyDown: (e) => e.preventDefault(),
                            sx: {
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#FF7B6B' },
                                '&.Mui-focused fieldset': { borderColor: '#FF7B6B' },
                                '& input': {
                                  cursor: 'pointer',
                                  caretColor: 'transparent'
                                }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            {/* Trip Description */}
            <Box sx={{ marginBottom: '40px' }}>
              <Typography sx={{
                display: 'block',
                marginBottom: '10px',
                fontWeight: 600,
                color: '#4A5568',
                fontSize: '0.95rem',
                letterSpacing: '0.5px'
              }}>
                Trip Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe your dream trip... What are you looking forward to? Any special occasions or must-see places?"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F7FAFC',
                    border: '2px solid #E2E8F0',
                    borderRadius: '15px',
                    color: '#4A5568',
                    lineHeight: 1.6,
                    '&:hover': {
                      backgroundColor: 'white',
                      borderColor: '#FF7B6B',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'white',
                      borderColor: '#FF7B6B',
                      boxShadow: '0 0 0 4px rgba(255, 123, 107, 0.1)'
                    },
                    '& fieldset': { border: 'none' }
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#666',
                    opacity: 1
                  }
                }}
              />

              {/* Additional trip context */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: { xs: '15px', sm: '20px' },
                marginTop: '20px'
              }}>
                <TextField
                  label="Traveler Age"
                  type="number"
                  value={formData.travelerAge}
                  onChange={(e) => handleInputChange('travelerAge', Number(e.target.value))}
                  fullWidth
                  InputProps={{ inputProps: { min: 0, max: 120 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F7FAFC',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#FF7B6B' },
                      '&.Mui-focused fieldset': { borderColor: '#FF7B6B' }
                    }
                  }}
                />
                <TextField
                  label="Number of Travelers"
                  type="number"
                  value={formData.numberOfTravelers}
                  onChange={(e) => handleInputChange('numberOfTravelers', Math.max(1, Number(e.target.value)))}
                  fullWidth
                  InputProps={{ inputProps: { min: 1 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F7FAFC',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#FF7B6B' },
                      '&.Mui-focused fieldset': { borderColor: '#FF7B6B' }
                    }
                  }}
                />
                <TextField
                  label="Wake-up Time"
                  type="time"
                  value={formData.wakeUpTime}
                  onChange={(e) => handleInputChange('wakeUpTime', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#F7FAFC',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#FF7B6B' },
                      '&.Mui-focused fieldset': { borderColor: '#FF7B6B' }
                    }
                  }}
                />
              </Box>
            </Box>

            {/* Travel Style */}
            <Box sx={{ marginBottom: '40px' }}>
              <Typography sx={{
                display: 'block',
                marginBottom: '15px',
                fontWeight: 600,
                color: '#e0e0e0',
                fontSize: '0.95rem',
                letterSpacing: '0.5px'
              }}>
                Travel Style
              </Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: 'repeat(2, 1fr)', 
                  sm: 'repeat(3, 1fr)', 
                  md: 'repeat(auto-fit, minmax(120px, 1fr))' 
                }, // Responsive grid
                gap: { xs: '10px', sm: '15px' } // Responsive gap
              }}>
                {travelStyles.map((style) => (
                  <Box
                    key={style.name}
                    onClick={() => handleStyleToggle(style.name)}
                    sx={{
                      padding: { xs: '15px 10px', sm: '20px' }, // Responsive padding
                      backgroundColor: selectedStyles.includes(style.name) 
                        ? 'rgba(102, 126, 234, 0.1)' 
                        : 'rgba(255, 255, 255, 0.05)',
                      border: selectedStyles.includes(style.name)
                        ? '2px solid #667eea'
                        : '2px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '15px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        borderColor: '#667eea'
                      }
                    }}
                  >
                    <Typography sx={{ 
                      fontSize: { xs: '1.5rem', sm: '2rem' }, // Responsive emoji size
                      marginBottom: '10px' 
                    }}>
                      {style.emoji}
                    </Typography>
                    <Typography sx={{ 
                      fontWeight: 600, 
                      fontSize: { xs: '0.8rem', sm: '0.95rem' } // Responsive text size
                    }}>
                      {style.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Budget Slider */}
            <Box sx={{ marginBottom: '50px' }}>
              <Typography sx={{
                display: 'block',
                marginBottom: '20px',
                fontWeight: 600,
                color: '#e0e0e0',
                fontSize: '0.95rem',
                letterSpacing: '0.5px'
              }}>
                Budget per Person
              </Typography>
              <Box sx={{ padding: '10px 0' }}>
                <Slider
                  value={formData.totalBudget}
                  onChange={handleBudgetChange}
                  min={500}
                  max={10000}
                  step={100}
                  sx={{
                    color: '#667eea',
                    height: 8,
                    '& .MuiSlider-thumb': {
                      height: 24,
                      width: 24,
                      backgroundColor: '#667eea',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 2px 10px rgba(102, 126, 234, 0.3)',
                      '&:hover': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.5)',
                        transform: 'scale(1.2)'
                      }
                    },
                    '& .MuiSlider-track': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none'
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginTop: '10px' 
                }}>
                  <Typography>Budget:</Typography>
                  <Typography sx={{ 
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    color: '#667eea'
                  }}>
                    ${formData.totalBudget.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Itinerary Section */}
            <Box sx={{
              marginTop: '50px',
              paddingTop: '50px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                marginBottom: '30px'
              }}>
                <Typography variant="h4" sx={{
                  fontSize: { xs: '1.5rem', sm: '1.8rem' },
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 50%, #FFA07A 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Your Itinerary
                </Typography>
                <Button
                  onClick={handleDirectAIGeneration}
                  disabled={loadingAI}
                  startIcon={loadingAI ? <CircularProgress size={18} color="inherit" /> : (
                    <Box component="svg" sx={{ 
                      width: { xs: '16px', sm: '18px' }, 
                      height: { xs: '16px', sm: '18px' }
                    }}>
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </Box>
                  )}
                  sx={{
                    background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 100%)',
                    color: 'white',
                    border: 'none',
                    padding: { xs: '12px 24px', sm: '14px 32px' },
                    borderRadius: '12px',
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: { xs: 'auto', sm: 'auto' },
                    alignSelf: { xs: 'flex-start', sm: 'auto' },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF8A7B 0%, #FF6B5B 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 20px rgba(255, 123, 107, 0.4)'
                    },
                    '&.Mui-disabled': {
                      color: 'rgba(255,255,255,0.8)'
                    }
                  }}
                >
                  {loadingAI ? 'Generating...' : 'Generate with AI'}
                </Button>
              </Box>

              {/* Itinerary Days */}
              <Box sx={{ marginTop: '30px' }}>
                {Array.from({ length: formData.dayCount }, (_, index) => {
                  const dayNumber = index + 1;
                  const dayEventsForDay = groupedEvents[dayNumber] || [];
                  
                  return (
                    <Box
                      key={dayNumber}
                      sx={{
                        background: 'rgba(255, 255, 255, 0.6)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '20px',
                        marginBottom: '20px',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: 'rgba(102, 126, 234, 0.3)',
                          boxShadow: '0 10px 30px rgba(102, 126, 234, 0.1)'
                        }
                      }}
                    >
                      {/* Day Header */}
                      <Box
                        onClick={() => toggleDayExpansion(dayNumber)}
                        sx={{
                          padding: '25px 30px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.3s ease',
                          background: 'rgba(255, 255, 255, 0.02)',
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{
                            fontSize: '1.4rem',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            marginBottom: '5px'
                          }}>
                            Day {dayNumber}
                          </Typography>
                          <Typography sx={{
                            fontSize: '0.9rem',
                            color: '#a0a0a0',
                            marginBottom: '5px'
                          }}>
                            {getDayDate(dayNumber)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          <Typography sx={{
                            fontSize: '0.9rem',
                            color: '#667eea',
                            fontWeight: 600,
                            padding: '5px 12px',
                            background: 'rgba(102, 126, 234, 0.1)',
                            borderRadius: '20px'
                          }}>
                            {dayEventsForDay.length} events
                          </Typography>
                          <ExpandMoreIcon sx={{
                            transition: 'transform 0.3s ease',
                            color: '#a0a0a0',
                            transform: expandedDays[dayNumber] ? 'rotate(180deg)' : 'rotate(0deg)'
                          }} />
                        </Box>
                      </Box>

                      {/* Day Events */}
                      <Collapse in={expandedDays[dayNumber]}>
                        <Box sx={{ padding: '0 30px 25px' }}>
                          {dayEventsForDay.map((event, eventIndex) => (
                            <Box
                              key={eventIndex}
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '20px',
                                padding: '20px',
                                background: 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '15px',
                                marginBottom: '15px',
                                border: '1px solid rgba(255, 255, 255, 0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'rgba(255, 255, 255, 0.7)',
                                  borderColor: 'rgba(102, 126, 234, 0.2)',
                                  transform: 'translateX(5px)'
                                }
                              }}
                            >
                              <Typography sx={{
                                fontSize: '0.9rem',
                                fontWeight: 600,
                                color: '#667eea',
                                minWidth: '80px',
                                paddingTop: '2px'
                              }}>
                                {event.start ? format(new Date(event.start), 'h:mm a') : `Event ${eventIndex + 1}`}
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                {/* Title - Changed color from #ffffff to #2D3748 for visibility */}
                                <Typography sx={{
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  marginBottom: '4px',
                                  color: '#2D3748' 
                                }}>
                                  {event.title}
                                </Typography>
                                
                                {/* Description */}
                                <Typography sx={{
                                  fontSize: '0.95rem',
                                  color: '#718096',
                                  lineHeight: 1.5,
                                  marginBottom: '4px'
                                }}>
                                  {event.description || 'No description'}
                                </Typography>

                                {/* Location - Now shown explicitly below description */}
                                {event.location && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#718096' }}>
                                    <span style={{ marginRight: '4px', fontSize: '1rem' }}>📍</span>
                                    <Typography sx={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                                      {event.location}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              <IconButton
                                size="small"
                                sx={{
                                  color: '#666',
                                  '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: '#a0a0a0'
                                  }
                                }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                          ))}
                          
                          {/* Add Event Button */}
                          <Button
                            fullWidth
                            onClick={() => handleAddEventToDay(dayNumber)}
                            sx={{
                              padding: '15px',
                              background: 'rgba(255, 255, 255, 0.02)',
                              border: '2px dashed rgba(255, 255, 255, 0.1)',
                              borderRadius: '15px',
                              color: '#666',
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                              '&:hover': {
                                borderColor: 'rgba(102, 126, 234, 0.5)',
                                color: '#667eea',
                                background: 'rgba(102, 126, 234, 0.05)'
                              }
                            }}
                          >
                            <AddIcon sx={{ marginRight: '8px' }} />
                            Add event
                          </Button>
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}

                {/* Add Day Button */}
                <Button
                  fullWidth
                  onClick={addNewDay}
                  sx={{
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '2px dashed rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    color: '#666',
                    fontWeight: 600,
                    textTransform: 'none',
                    marginTop: '20px',
                    '&:hover': {
                      borderColor: '#667eea',
                      color: '#667eea',
                      background: 'rgba(102, 126, 234, 0.05)',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <AddIcon sx={{ marginRight: '10px' }} />
                  Add Day
                </Button>
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: 'flex',
              gap: '20px',
              marginTop: '50px',
              justifyContent: 'center'
            }}>
              <Button
                type="submit"
                disabled={formData.destinations.length === 0}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '20px 60px',
                  borderRadius: '15px',
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 15px 40px rgba(102, 126, 234, 0.4)'
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: '#666'
                  }
                }}
              >
                {initialData ? 'Update Trip' : 'Create Trip'}
              </Button>
              
              {onCancel && (
                <Button
                  onClick={onCancel}
                  sx={{
                    background: 'transparent',
                    color: '#a0a0a0',
                    border: '2px solid rgba(255, 255, 255, 0.2)',
                    padding: '18px 50px',
                    borderRadius: '15px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#667eea',
                      color: '#667eea',
                      background: 'rgba(102, 126, 234, 0.05)'
                    }
                  }}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Event Creation Dialog */}
        <EventCreationDialog
          open={openEventDialog}
          onClose={handleCloseEventDialog}
          onSubmit={handleEventSubmit}
          selectedDay={selectedDay}
          dayDate={formData.startDate ? getDayDate(selectedDay) : null}
        />

        {/* Themed AI Loading Modal */}
        <Dialog
          open={loadingAI}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              borderRadius: '24px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%', // Mobile responsive
              textAlign: 'center',
              background: 'white',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
            }
          }}
          sx={{ zIndex: 9999 }}
        >
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Animated Icon */}
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
              <CircularProgress size={80} thickness={2} sx={{ color: '#FF7B6B' }} />
              <Box
                sx={{
                  top: 0, left: 0, bottom: 0, right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2.5rem'
                }}
              >
                🧳
              </Box>
            </Box>
            
            <Typography variant="h5" sx={{ 
              fontWeight: 800, 
              mb: 1,
              background: 'linear-gradient(135deg, #FF9A8B 0%, #FF7B6B 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Designing Your Trip
            </Typography>
            
            <Typography variant="body1" sx={{ 
              color: '#718096', 
              mb: 4, 
              minHeight: '24px',
              fontStyle: 'italic',
              fontWeight: 500,
              fontSize: '1.1rem'
            }}>
              {loadingMessage}
            </Typography>

            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: '#EDF2F7',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(90deg, #FF9A8B 0%, #FF7B6B 100%)',
                    borderRadius: 5
                  }
                }} 
              />
            </Box>
            
            <Typography variant="caption" sx={{ color: '#A0AEC0', fontSize: '0.8rem' }}>
              Please keep this tab open. Good things take time!
            </Typography>
          </Box>
        </Dialog>
      </Box>
    </Modal>
  );
};

export default TravelPlanForm; 